import React, { useState, useEffect, useMemo, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image, ActivityIndicator, Animated, Easing, Platform } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useSidebar } from '../../context/SidebarContext';
import { useNavigation } from '@react-navigation/native';
import { useWindowDimensions } from 'react-native';
import apiClient, { timetableAPI } from '../../services/api';
import { isAwayToday } from '../../utils/todayAttendance';
import { parentGreetingName, relativesOf } from '../../utils/parentGreeting';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { Student } from '../../types';
import { useCxcOptions } from '../../hooks/useCxcOptions';
import { cxcSittings, isCxcRegistrationOpen, isStudentEligibleForCxc } from '../../utils/cxcRegistration';
import { AnimatePresence, MotionDiv } from '../motion/Motion';
import { EASE_OUT } from '../../utils/motionPresets';
import { HeaderSearch } from './HeaderSearch';
import { showsSearchBox, usesMenuDrawer } from '../../utils/webLayout';

interface WebHeaderProps {
  title: string;
}

function getInitials(name?: string) {
  const parts = (name || '').trim().split(' ').filter(Boolean);
  if (parts.length > 1) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name || '').substring(0, 2).toUpperCase();
}

/**
 * Animated live blinking dot indicator with expanding radar ping wave.
 */
function CxcLiveDot() {
  const pulseAnim = useRef(new Animated.Value(0)).current;
  const blinkAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    // Ping ripple ring that radiates outward
    const pulseLoop = Animated.loop(
      Animated.timing(pulseAnim, {
        toValue: 1,
        duration: 1400,
        easing: Easing.out(Easing.ease),
        useNativeDriver: Platform.OS !== 'web',
      })
    );

    // Rhythmic live blinking / breathing effect
    const blinkLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(blinkAnim, {
          toValue: 0.25,
          duration: 550,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(blinkAnim, {
          toValue: 1,
          duration: 550,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: Platform.OS !== 'web',
        }),
      ])
    );

    pulseLoop.start();
    blinkLoop.start();

    return () => {
      pulseLoop.stop();
      blinkLoop.stop();
    };
  }, [pulseAnim, blinkAnim]);

  const ringScale = pulseAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 2.6],
  });

  const ringOpacity = pulseAnim.interpolate({
    inputRange: [0, 0.6, 1],
    outputRange: [0.85, 0.3, 0],
  });

  const dotScale = blinkAnim.interpolate({
    inputRange: [0.25, 1],
    outputRange: [0.85, 1.15],
  });

  return (
    <View style={cxcLiveDotStyles.container}>
      <Animated.View
        style={[
          cxcLiveDotStyles.ring,
          {
            transform: [{ scale: ringScale }],
            opacity: ringOpacity,
          },
        ]}
      />
      <Animated.View
        style={[
          cxcLiveDotStyles.dot,
          {
            opacity: blinkAnim,
            transform: [{ scale: dotScale }],
          },
        ]}
      />
    </View>
  );
}

const cxcLiveDotStyles = StyleSheet.create({
  container: {
    width: 8,
    height: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 5,
    position: 'relative',
  },
  ring: {
    position: 'absolute',
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#10B981',
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#10B981',
    shadowColor: '#10B981',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.9,
    shadowRadius: 4,
    elevation: 3,
  },
});

export function WebHeader({ title }: WebHeaderProps) {
  const { user, students, selectedStudent, selectStudent, logout } = useAuth();

  // The same greeting the app's header shows ("Good evening, Mr. Morgan"), by the device's clock
  // and checked every minute so it moves on by itself (27 Oct 2026).
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  const [greeting, setGreeting] = useState(getGreeting);
  useEffect(() => {
    const timer = setInterval(() => setGreeting(getGreeting()), 60_000);
    return () => clearInterval(timer);
  }, []);
  // The signed-in parent as the school records them (utils/parentGreeting), so a corrected
  // name shows here without signing out.
  const parentName = parentGreetingName(user, relativesOf(selectedStudent));
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [switchingStudentId, setSwitchingStudentId] = useState<string | null>(null);
  const hasMultipleChildren = students.length > 1;

  const handleSelectStudent = async (student: Student) => {
    setSwitchingStudentId(student.id);
    await new Promise(resolve => setTimeout(resolve, 1200));
    await selectStudent(student);
    setSwitchingStudentId(null);
    setShowStudentDropdown(false);
  };
  const { unreadNotificationCount } = useNotifications();
  const { toggleSidebar } = useSidebar();
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  // Tablets too: the page menu is a drawer there (utils/webLayout), opened from this button.
  const showMenuButton = usesMenuDrawer(width);
  // The full search box where the header has room for it beside the title; else the icon.
  const searchBox = showsSearchBox(width);
  // Room for everything: below this the class chip would squeeze down to a bare dot, so it
  // and the search's shortcut hint step aside for the search box.
  const isWide = width >= 1200;
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  // The navy band (approved design A, 2 Nov 2026). react-native-web hands backgroundImage
  // to the browser, as @shared/commerce/Gradient.web.tsx does; backgroundColor is the fallback.
  const band = useMemo(
    () => ({ backgroundImage: `linear-gradient(100deg, ${colors.headerFrom} 0%, ${colors.headerTo} 100%)` }) as any,
    [colors],
  );

  const [attendanceStatus, setAttendanceStatus] = useState<'present' | 'absent' | 'leave' | null>(null);
  const [currentClass, setCurrentClass] = useState<string | null>(null);
  // Open/closed as the server reports it; the highlight waits for its answer.
  const { options: cxcOptions } = useCxcOptions();
  const cxcOpen = useMemo(() => isCxcRegistrationOpen(cxcSittings(cxcOptions)), [cxcOptions]);
  const isCxcEligible = isStudentEligibleForCxc(selectedStudent);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (!selectedStudent?.id || !selectedStudent?.classId) return;
      try {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth() + 1;

        const response = await apiClient.get(
          `/student/attendance/student/${selectedStudent.id}/class/${selectedStudent.classId}?year=${year}&month=${month}`
        );

        let dataArray = null;
        if (Array.isArray(response.data)) {
          dataArray = response.data;
        } else if (response.data && Array.isArray(response.data.days)) {
          dataArray = response.data.days;
        } else if (response.data && Array.isArray(response.data.data)) {
          dataArray = response.data.data;
        }

        if (isMounted && dataArray) {
          const yyyy = today.getFullYear();
          const mm = String(today.getMonth() + 1).padStart(2, '0');
          const dd = String(today.getDate()).padStart(2, '0');
          const todayStr = `${yyyy}-${mm}-${dd}`;

          const todayRecord = dataArray.find((record: any) => {
            return record.date && String(record.date).startsWith(todayStr);
          });

          if (todayRecord && todayRecord.status) {
            setAttendanceStatus(String(todayRecord.status).trim().toLowerCase() as any);
          } else {
            setAttendanceStatus(null);
          }
        }
      } catch (err) {
        console.error('Failed to fetch attendance for header:', err);
      }

      try {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        const todayStr = `${yyyy}-${mm}-${dd}`;

        const timetableRes = await timetableAPI.getTimetable(
          selectedStudent.classId,
          todayStr,
          'weekly'
        );

        if (isMounted) {
          const daysArray = timetableRes.data?.days ?? [];
          const dayOfWeek = today.getDay();
          const todayData = daysArray.find((d: any) => d.dayOfWeek === dayOfWeek);

          if (todayData && todayData.slots && todayData.slots.length > 0) {
            const currentMinutes = today.getHours() * 60 + today.getMinutes();

            const parseTime = (timeStr: string) => {
              if (!timeStr) return 0;
              const parts = timeStr.split(':');
              return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
            };

            const ongoingSlot = todayData.slots.find((slot: any) => {
              const start = parseTime(slot.startTime);
              const end = parseTime(slot.endTime);
              return currentMinutes >= start && currentMinutes <= end;
            });

            if (ongoingSlot) {
              const isBreak = ongoingSlot.isTeachingSlot === false;
              if (isBreak) {
                setCurrentClass((ongoingSlot.slotName || 'Break').toUpperCase());
              } else {
                setCurrentClass(`In class • ${ongoingSlot.subjectName || ongoingSlot.slotName || 'Unknown'}`);
              }
            } else {
              const firstSlotStart = Math.min(...todayData.slots.map((s: any) => parseTime(s.startTime)));
              const lastSlotEnd = Math.max(...todayData.slots.map((s: any) => parseTime(s.endTime)));

              if (currentMinutes < firstSlotStart) {
                setCurrentClass('Classes not started');
              } else if (currentMinutes > lastSlotEnd) {
                setCurrentClass('Classes ended');
              } else {
                setCurrentClass('Free period');
              }
            }
          } else {
            setCurrentClass('No classes');
          }
        }
      } catch (err) {
        console.error('Failed to fetch timetable for header:', err);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [selectedStudent]);

  return (
    <View style={[styles.header, band, isMobile && styles.headerMobile]}>
      <View style={styles.leftSection}>
        {showMenuButton && (
          <TouchableOpacity onPress={toggleSidebar} style={styles.menuButton} accessibilityLabel="Open menu">
            <FeatherIcon name="menu" size={24} color={colors.headerText} />
          </TouchableOpacity>
        )}
        <View style={styles.titleBlock}>
          {!!parentName && (
            <Text style={styles.greeting} numberOfLines={1} testID="web-header-greeting">{`${greeting}, ${parentName}`}</Text>
          )}
          <Text style={styles.title} numberOfLines={1} ellipsizeMode="tail">{title}</Text>
        </View>
      </View>

      {searchBox && (
        <HeaderSearch compact={false} showShortcut={isWide} cxcOpen={cxcOpen && isCxcEligible} />
      )}

      <View style={[styles.rightActions, isMobile && styles.rightActionsMobile]}>
        {/* CXC Registration Highlight Badge: only for students in Year 7+ */}
        {cxcOpen && isCxcEligible && (
          <TouchableOpacity
            style={[styles.cxcHeaderBadge, isMobile && styles.cxcHeaderBadgeMobile]}
            onPress={() => navigation.navigate('CxcRegistration')}
            activeOpacity={0.82}
            accessibilityLabel="CXC Examination Registration Open"
          >
            <View style={styles.cxcBadgeIconWrapper}>
              <FeatherIcon name="award" size={13} color="#FBBF24" />
            </View>
            {!isMobile && <Text style={styles.cxcBadgeLabel}>CXC Registration</Text>}
            <View style={styles.cxcBadgeHighlight}>
              <CxcLiveDot />
              <Text style={styles.cxcBadgeHighlightText}>OPEN</Text>
            </View>
          </TouchableOpacity>
        )}

        {/* Status Badges */}
        {/* On leave or absent today: only the attendance chip, never what the class is doing. */}
        {currentClass && isWide && !isAwayToday(attendanceStatus) && (
          <View style={styles.statusBadgeDark}>
            <View style={styles.dotGreen} />
            <Text style={styles.statusTextWhite} numberOfLines={1}>{currentClass}</Text>
          </View>
        )}

        {/* User Profile / Student Switcher */}
        <View style={{ position: 'relative' }}>
          <TouchableOpacity
            style={[styles.profileDropdown, isMobile && styles.profileDropdownMobile]}
            onPress={() => hasMultipleChildren && setShowStudentDropdown(v => !v)}
            disabled={!hasMultipleChildren}
          >
            <View style={styles.avatarBlue}>
              <Text style={styles.avatarText}>{selectedStudent ? getInitials(selectedStudent.name) : ''}</Text>
            </View>
            {!isMobile && (
              <Text style={styles.profileName} numberOfLines={1}>{selectedStudent ? selectedStudent.name : ''}</Text>
            )}
            {hasMultipleChildren && !isMobile && (
              <FeatherIcon
                name={showStudentDropdown ? 'chevron-up' : 'chevron-down'}
                size={16}
                color={colors.headerTextMuted}
                style={{ marginLeft: 8 }}
              />
            )}
          </TouchableOpacity>

          {hasMultipleChildren && showStudentDropdown && (
            <TouchableOpacity
              style={styles.dropdownBackdrop}
              activeOpacity={1}
              onPress={() => setShowStudentDropdown(false)}
            />
          )}
          {/* The switcher drops open from the button and folds back into it. */}
          <AnimatePresence>
            {hasMultipleChildren && showStudentDropdown && (
              <MotionDiv
                key="student-switcher"
                style={studentSwitcherPosition}
                initial={{ opacity: 0, y: -6, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.97 }}
                transition={{ duration: 0.18, ease: EASE_OUT }}
              >
                <View style={styles.studentDropdown}>
                  {students.map((student) => {
                    const isSelected = student.id === selectedStudent?.id;
                    const isSwitching = switchingStudentId === student.id;
                    return (
                      <TouchableOpacity
                        key={student.id}
                        style={[styles.studentDropdownItem, isSelected && styles.studentDropdownItemSelected]}
                        onPress={() => handleSelectStudent(student)}
                        disabled={!!switchingStudentId}
                      >
                        <View style={styles.studentDropdownAvatar}>
                          <Text style={styles.studentDropdownAvatarText}>{getInitials(student.name)}</Text>
                        </View>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.studentDropdownName}>{student.name}</Text>
                          <Text style={styles.studentDropdownClass}>{student.class}</Text>
                        </View>
                        {isSwitching ? (
                          <ActivityIndicator size="small" color={colors.primary} />
                        ) : isSelected ? (
                          <FeatherIcon name="check" size={18} color={colors.primary} />
                        ) : null}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </MotionDiv>
            )}
          </AnimatePresence>
        </View>

        {/* Attendance Badge */}
        {attendanceStatus === 'present' && (
          <View style={[styles.statusChipLightGreen, isMobile && styles.statusChipMobile]}>
            <FeatherIcon name="check" size={14} color="#6EE7B7" />
            {!isMobile && <Text style={styles.statusTextGreen}> Present</Text>}
          </View>
        )}
        {attendanceStatus === 'absent' && (
          <View style={[styles.statusChipLightRed, isMobile && styles.statusChipMobile]}>
            <FeatherIcon name="x" size={14} color="#FCA5A5" />
            {!isMobile && <Text style={styles.statusTextRed}> Absent</Text>}
          </View>
        )}
        {attendanceStatus === 'leave' && (
          <View style={[styles.statusChipLightOrange, isMobile && styles.statusChipMobile]}>
            <FeatherIcon name="sun" size={14} color="#FCD34D" />
            {!isMobile && <Text style={styles.statusTextOrange}> Leave</Text>}
          </View>
        )}

        {/* Phones, tablets and narrow laptops: search sits with the buttons and opens as a sheet over the page. */}
        {!searchBox && (
          <HeaderSearch compact cxcOpen={cxcOpen && isCxcEligible} />
        )}

        {/* Notifications */}
        <TouchableOpacity style={styles.iconButton} onPress={() => navigation.navigate('Notifications')}>
          <FeatherIcon name="bell" size={18} color={colors.headerText} />
          {unreadNotificationCount > 0 && (
            <View style={styles.notificationDot}>
              <Text style={styles.notificationBadgeText}>
                {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
              </Text>
            </View>
          )}
        </TouchableOpacity>

        {/* Logout */}
        {!isMobile && (
          <TouchableOpacity style={styles.iconButtonOutlined} onPress={logout}>
            <FeatherIcon name="log-out" size={18} color="#FCA5A5" />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

// Plain CSS for the switcher's MotionDiv (a DOM div on web): where the menu sits, and the
// corner it grows from - the button it opens under.
const studentSwitcherPosition = {
  position: 'absolute',
  top: 48,
  right: 0,
  zIndex: 1000,
  transformOrigin: 'top right',
};

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  // The gradient itself is `band`, set inline where the header is drawn.
  header: {
    height: 72,
    backgroundColor: colors.headerFrom,
    borderBottomWidth: 3,
    borderBottomColor: colors.headerAccent,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 32,
    zIndex: 100,
    position: 'sticky' as any,
    top: 0,
  },
  headerMobile: {
    paddingHorizontal: 16,
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    minWidth: 0,
    marginRight: 12,
  },
  menuButton: {
    marginRight: 16,
  },
  titleBlock: {
    flexShrink: 1,
    minWidth: 0,
  },
  greeting: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.headerTextMuted,
    marginBottom: 2,
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.headerText,
    flexShrink: 1,
  },
  rightActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    // Never squeezed: its buttons can't shrink, so they ran past the right edge (tablet, page
    // with a scrollbar). The title shortens with "…" and the search box narrows instead.
    flexShrink: 0,
  },
  rightActionsMobile: {
    gap: 8,
  },
  // CXC Registration Header Highlight Badge
  cxcHeaderBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    backgroundColor: '#0F172A',
    borderWidth: 1,
    borderColor: 'rgba(245, 158, 11, 0.45)',
    paddingHorizontal: 11,
    paddingVertical: 5,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  cxcHeaderBadgeMobile: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 5,
  },
  cxcBadgeIconWrapper: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: 'rgba(245, 158, 11, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cxcBadgeLabel: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.2,
  },
  cxcBadgeHighlight: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(16, 185, 129, 0.2)',
    borderWidth: 1,
    borderColor: 'rgba(16, 185, 129, 0.5)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
  },
  cxcPulseDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#10B981',
    marginRight: 4,
  },
  cxcBadgeHighlightText: {
    color: '#34D399',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.4,
  },
  // maxWidth + flexShrink keep a long subject name ("In class • …") inside the
  // chip so it ellipsizes rather than squeezing the badges beside it.
  statusBadgeDark: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.headerControl,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    flexShrink: 1,
    maxWidth: 260,
  },
  dotGreen: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#10B981',
    marginRight: 6,
  },
  statusTextWhite: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
    flexShrink: 1,
  },

  profileDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.headerControl,
    borderWidth: 1,
    borderColor: colors.headerControlBorder,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 24,
    flexShrink: 1,
    maxWidth: 220,
  },
  profileDropdownMobile: {
    maxWidth: 44,
    paddingHorizontal: 4,
    paddingVertical: 4,
    borderWidth: 0,
    backgroundColor: 'transparent',
  },
  avatarBlue: {
    width: 28,
    height: 28,
    backgroundColor: colors.primary,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  avatarText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '700',
  },
  profileName: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.headerText,
    flexShrink: 1,
  },
  // Today's attendance, as glassy tints that read on the navy band.
  statusChipLightGreen: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(16, 185, 129, 0.2)',
    borderWidth: 1,
    borderColor: 'rgba(16, 185, 129, 0.5)',
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 20,
    marginLeft: 8,
  },
  statusTextGreen: { color: '#A7F3D0', fontSize: 13, fontWeight: '600' },
  statusChipLightRed: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.5)',
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 20,
    marginLeft: 8,
  },
  statusTextRed: { color: '#FECACA', fontSize: 13, fontWeight: '600' },
  statusChipLightOrange: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(245, 158, 11, 0.2)',
    borderWidth: 1,
    borderColor: 'rgba(245, 158, 11, 0.5)',
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 20,
    marginLeft: 8,
  },
  statusTextOrange: { color: '#FDE68A', fontSize: 13, fontWeight: '600' },
  statusChipMobile: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    marginLeft: 0,
  },
  iconButton: {
    width: 38,
    height: 38,
    borderRadius: 11,
    backgroundColor: colors.headerControl,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  notificationDot: {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#EF4444',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
    borderWidth: 1.5,
    borderColor: colors.headerFrom,
  },
  notificationBadgeText: {
    color: '#FFFFFF',
    fontSize: 9,
    fontWeight: 'bold',
  },
  iconButtonOutlined: {
    width: 38,
    height: 38,
    borderRadius: 11,
    backgroundColor: 'rgba(239, 68, 68, 0.16)',
    alignItems: 'center',
    justifyContent: 'center',
  },

  dropdownBackdrop: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 900,
  },
  // Positioned by studentSwitcherPosition, the animated wrapper around it.
  studentDropdown: {
    width: 260,
    backgroundColor: colors.surface,
    borderRadius: 16,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: colors.border,
    elevation: 6,
    shadowColor: colors.shadow,
    shadowOpacity: 0.12,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
  },
  studentDropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 12,
  } as any,
  studentDropdownItemSelected: {
    backgroundColor: colors.primaryLight,
  },
  studentDropdownAvatar: {
    width: 32,
    height: 32,
    borderRadius: 9,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  studentDropdownAvatarText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '700',
  },
  studentDropdownName: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  studentDropdownClass: {
    fontSize: 12,
    color: colors.textSecondary,
    fontWeight: '500',
    marginTop: 2,
  },
});
